import { msg } from "@omegagrid/localize";
import { CellModel, CellSource, ColumnSource, RequestFilters, RowSource } from "@omegagrid/grid";
import rp, { GridEditorAdapter, GridEditorOptions, formatSeconds } from "@reporting/rp-app-host";
import { JobActivity } from "@reporting/rp-types/server";
import { JOB_RUN_OUTCOMES, JOB_RUN_OUTCOME_MAP } from "../types";

const PAGE_SIZE = 100;

export class JobActivityMonitorGridAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];
	private _timerCompleteHandler?: EventListener;

	constructor(
		private idServer?: number
	) { super() }

    private ensureTimerListener() {
        if (this._timerCompleteHandler || !this.editor) return;
        this._timerCompleteHandler = () => {
			// delay 1 second before refreshing
			setTimeout(() => { this.editor.refresh(); }, 1000);
		};
        this.editor.addEventListener('complete', this._timerCompleteHandler);
    }

    dispose() {
		if (this._timerCompleteHandler && this.editor) {
			this.editor.removeEventListener('complete', this._timerCompleteHandler);
			this._timerCompleteHandler = undefined;
		}
    }

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		this._columns = [{
			t: 'a',
			w: 25,
			data: 'controls',
			filter: false,
			sorting: false,
		}, {
			data: '_status',
			header: msg('Status'),
			t: 's',
			content: JOB_RUN_OUTCOMES.map(o => ({
				k: o.id,
				v: msg(o.text),
				icon: o.icon ? { icon: o.icon, color: o.color ? o.color : undefined } : undefined
			})),
			w: 250
		}, {
			data: 'job_name',
			header: msg('Job'),
			t: 't',
			w: 160
		}, {
			data: 'enabled',
			header: msg('Enabled'),
			t: 'b',
			w: 70
		}, {
			data: 'start_execution_date',
			header: msg('Start Execution Date'),
			t: 'dt',
			w: 150
		}, {
			data: 'stop_execution_date',
			header: msg('Stop Execution Date'),
			t: 'dt',
			w: 150,
		}, {
			data: 'last_duration',
			header: msg('Last Duration'),
			t: 't',
			w: 120
		}, {
			data: 'last_outcome_message',
			header: msg('Last Outcome Message'),
			t: 't',
			w: 200
		}, {
			data: 'next_scheduled_run_date',
			header: msg('Next Scheduled Run Date'),
			t: 'dt',
			w: 265
		}, {
			data: 'description',
			header: msg('Description'),
			t: 't',
			w: 300
		}, {
			data: 'company',
			header: msg('Company'),
			t: 't',
			w: 200
		}, {
			data: 'accounting_hidden',
			header: msg('Hidden'),
			t: 'b',
			w: 60
		}, {
			data: 'who_created',
			header: msg('User'),
			t: 't',
			w: 120
		}];
		return this._columns;
	}

	async getRows(filters?: RequestFilters): Promise<RowSource[]> {
		const rows = await rp.ms('server').get<JobActivity[]>(`/jobActivity/${this.idServer}`, this.createRequestParams(
			filters,
			{ count: PAGE_SIZE },
			{ _status: 'last_run_outcome' }
		));

		return rows.map(data => ({
			data: {
				...data,
				_status: this.getStatusCell(data),
				company: { v: data.company, dv: `${data.accounting} ${data.company}` },
				last_duration: { v: data.last_duration, dv: formatSeconds(data.last_duration) },
				
				controls: { v: [{
					action: 'link',
					icon: 'arrow-up-right-from-square',
					color: 'gray',
					title: `#${data.accounting} ${data.company || `[${msg('unknown')}]`}: ETL`
				}] }
			},
			s: { color: data.accounting_hidden ? 'ff0000' : '' },
		}));
	}

	getStatusCell(data: JobActivity): CellSource {
		const cell = { t: 'h' } as CellSource;
		if (data.running) {
			const start = data.start_execution_date ? new Date(data.start_execution_date) : null;
            const startIso = start && !Number.isNaN(start.getTime()) ? start.toISOString() : '';
            cell.v = `<span>
						<og-loader icon="spinner" style="display: contents"></og-loader>
						<rp-job-timer start="${startIso}" last="${data.last_duration}" step="${data.step_name}"></rp-job-timer>
					</span>`;
			this.ensureTimerListener();
		} else {
			const outcome = JOB_RUN_OUTCOME_MAP.get(data.last_run_outcome);
			if (outcome) {
				cell.v = `<span ${outcome.color ? `style="color: ${outcome.color}"` : ''}>
							${outcome.icon ? `<og-icon icon="${outcome.icon}"></og-icon>` : ''}
							${msg(outcome.text)}
						</span>`;
			} else {
				cell.v = '';
			}
		}
		return cell;
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			columnSorting: true,
			filters: true,
			pageSize: PAGE_SIZE,
			editable: false,
			lightTheme: null,
			darkTheme: null,
			freezeLeft: 1,
			toolbar: {
				autoUpdate: {enabled: true},
				save: {enabled: false},
				discard: {enabled: false},
				new: {enabled: false},
			},
		});
	}

	onAction(action: string, cell: CellModel): void {
		if (action === 'link') {
			window.open(rp.router.getUrl({a: cell.row.data.accounting, app: 'etl'}));
		}
	}

}
